import { useBackButton } from '@ionic/vue';
import { useRouter } from 'vue-router';
import { LINKS } from '@/utils/links';
import { setGoingBack } from '@/animations/navigation-direction';
import {
  isXStateFlowActive,
  getActiveXStateFlow,
  clearXStateFlow,
} from '@/composables/useXStateFlow';

/**
 * Behavior:
 * - When on a completed flow end screen: redirects to the slot detail page
 * - When an XState flow is active: sends BACK or exits if no BACK transition
 * - When no flow is active: delegates to the next Ionic back button handler
 *
 * Uses Ionic's priority-based back button system (priority 10) to avoid
 * conflicting with IonRouterOutlet (priority 0) and overlays (priority 100).
 */
export function useFlowBackButton(): void {
  const router = useRouter();

  useBackButton(10, (processNextHandler) => {
    const currentRoute = router.currentRoute.value;
    const step = currentRoute.meta?.step as string | undefined;

    // Block back navigation when the parcours is finished
    if (step === 'end' || step === 'bad-end') {
      const { missionId, slotId } = currentRoute.params;
      if (missionId && slotId) {
        router.replace(LINKS.MISSIONS.SLOT.ID(missionId, slotId));
      }
      return;
    }

    if (isXStateFlowActive()) {
      const flow = getActiveXStateFlow();
      if (flow) {
        const snapshot = flow.actor.getSnapshot();
        const canBack = snapshot.can({ type: 'BACK' });
        if (canBack) {
          setGoingBack(true);
          flow.actor.send({ type: 'BACK' });
        } else {
          const exitRoute = flow.config.exitRoute;
          clearXStateFlow();
          setGoingBack(true);
          if (exitRoute) {
            router.replace(exitRoute);
          } else if ((window.history.state?.position ?? 0) > 0) {
            router.back();
          } else {
            router.replace(LINKS.TABS.DASHBOARD);
          }
        }
        return; // Duplicated => Block NativeRouter from also popping
      }
    }
    // No active flow — let Ionic handle normally
    processNextHandler();
  });
}
